import { Tooltip } from 'bootstrap';
import { useContext, useEffect, useRef, useState } from 'react';
import { ReportAlumniRow } from './WatchlistReportService';
import SessionContext from '../../../library/Core/SessionContext';
import { getStageDescriptions } from './labelMaps';

// Stage badge with a Bootstrap tooltip showing the outcome-specific stage description.
const StageBadge = ({ stage, label, outcome }: { stage: string | null; label: string | null; outcome: string | null }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const description = stage && outcome ? (getStageDescriptions(outcome)?.[stage] ?? label) : null;

    useEffect(() => {
        if (!ref.current || !description) return;
        const tooltip = new Tooltip(ref.current, { container: 'body' });
        return () => tooltip.dispose();
    }, [description]);

    if (!label) return <span className="text-muted">—</span>;
    return (
        <span
            ref={ref}
            className="badge bg-secondary bg-opacity-10 text-dark-blue font-11 text-capitalize"
            data-bs-toggle="tooltip"
            data-bs-title={description ?? label}>
            {label}
        </span>
    );
};

// Alumni name with evidence_text hover tooltip — same pattern as the dashboard AlumniRow.
const AlumniName = ({
    name,
    studentId,
    evidenceText,
    hasPendingReview,
}: {
    name: string | null;
    studentId: string;
    evidenceText: string | null;
    hasPendingReview: boolean;
}) => {
    const nameRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (!nameRef.current || !evidenceText) return;
        const tooltip = new Tooltip(nameRef.current, { container: 'body' });
        return () => tooltip.dispose();
    }, [evidenceText]);

    return (
        <div className="font-600 text-dark-blue">
            <a
                ref={nameRef}
                href={`/alumni/${studentId}/profile`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-600 text-dark-blue text-decoration-none d-inline-block"
                data-bs-toggle={evidenceText ? 'tooltip' : undefined}
                data-bs-title={evidenceText ?? undefined}>
                {name ?? '—'}
            </a>
            {hasPendingReview && <i className="bi bi-clock-history ms-1 text-warning" title="Has a pending HITL review"></i>}
        </div>
    );
};

export type RowAction =
    | 'markReady' | 'unmarkReady' | 'changePhase' | 'markDormant' | 'markActive' | 'captureCommitment'
    | 'advanceStage' | 'markDenied' | 'clearOverride' | 'addToCustom' | 'viewProfile';

// Context-aware action set per layer (spec §9.1).
const actionsForRow = (row: ReportAlumniRow): { key: RowAction; label: string }[] => {
    const items: { key: RowAction; label: string }[] = [];
    if (!row.is_pitch_ready) {
        items.push({ key: 'markReady', label: '🎯 Mark Ready for Pitch' });
    } else {
        items.push({ key: 'changePhase', label: '🔁 Change Pitch Phase' });
        items.push({ key: 'unmarkReady', label: '🎯 Unmark Pitch Ready' });
    }

    if (row.layer === 'activation') {
        // Denied is terminal — advancing or re-denying is meaningless. (Clear Override reverts it.)
        // Mark Dormant is hidden when already dormant.
        const isDenied = row.stage === 'denied';
        const isDormant = row.stage === 'dormant';
        if (!isDenied) items.push({ key: 'advanceStage', label: '⬆️ Advance Stage' });
        if (!isDormant) items.push({ key: 'markDormant', label: '⬇️ Mark Dormant' });
        if (!isDenied) items.push({ key: 'markDenied', label: '❌ Mark Denied' });
    } else if (row.layer === 'strategic') {
        items.push({ key: 'markDormant', label: '💤 Mark Dormant' });
    } else {
        // signal
        // if (row.watchlist_code === 'dormant_alumni') items.push({ key: 'markActive', label: '⚡ Mark Active' });
        // else 
        if (row.watchlist_code !== 'dormant_alumni') items.push({ key: 'markDormant', label: '💤 Mark Dormant' });
    }

    // items.push({ key: 'addToCustom', label: '📌 Add to Custom Watchlist' });
    items.push({ key: 'viewProfile', label: '👤 View Profile' });
    items.push({ key: 'captureCommitment', label: '🤝 Capture Commitment' });
    if (row.is_manual_override) items.push({ key: 'clearOverride', label: '🔄 Clear Override' });
    return items;
};

// Engagement-signal chip: shows the value's first letter (H / M / L), with the full title and value
// on hover (e.g. "Alma Mater Pride: HIGH"). Values are the strings 'HIGH' | 'MODERATE' | 'LOW'.
// Colour encodes the VALUE: High → green, Moderate → blue, Low → red, so the signal reads at a glance.
const toneForValue = (value: string | null): string => {
    switch ((value ?? '').toUpperCase()) {
        case 'HIGH': return 'bg-success text-success';
        case 'MODERATE': return 'bg-info text-info';
        case 'LOW': return 'bg-danger text-danger';
        default: return 'bg-secondary text-secondary';
    }
};

const SignalChip = ({ initial, title, value }: { initial: string; title: string; value: string | null }) => (
    <span 
    style={{
        height: '20px',
        width: '20px'
    }}
    className={`badge d-flex align-items-center justify-content-center ${toneForValue(value)} bg-opacity-10 font-10 me-1`} title={value ? `${title}: ${value}` : title}>
        {initial} {value ? value.charAt(0).toUpperCase() : '—'}
    </span>
);

// Layer badge — clarifies which layer a watchlist row belongs to (e.g. Signal vs Activation),
// so the same alumnus appearing in multiple watchlists reads correctly.
const LAYER_BADGE: Record<string, string> = {
    signal: 'bg-primary bg-opacity-10 text-primary',
    strategic: 'bg-bs-purple bg-opacity-10 text-primary-emphasis',
    activation: 'bg-success bg-opacity-10 text-success',
};
const LAYER_LABEL: Record<string, string> = { signal: 'Signal', strategic: 'Strategic', activation: 'Activation' };

/** Format API date (yyyy-mm-dd) as dd-mmm-yyyy (e.g. 03-Aug-2026). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatAddedOn = (iso: string | null): string => {
    if (!iso) return '—';
    const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    const month = MONTHS[Number(m[2]) - 1];
    if (!month) return iso;
    return `${m[3]}-${month}-${m[1]}`;
};

const WatchlistReportTable = ({
    rows,
    onAction,
}: {
    rows: ReportAlumniRow[];
    onAction: (action: RowAction, row: ReportAlumniRow) => void;
}) => {
    const { sessionState } = useContext(SessionContext);
    const isInstituteAdmin = sessionState?.userTypeCode === 'INSTITUTE_ADMIN';
    const [openId, setOpenId] = useState<string | null>(null);

    return (
        <div className="table-responsive bg-white border rounded-3 shadow-sm" style={{ minHeight:400 }}>
            <table className="table table-hover align-middle mb-0">
                <thead className="table-light text-muted font-11 text-uppercase">
                    <tr>
                        <th className="px-3 py-2">Alumni</th>
                        <th className="px-3 py-2">Program / Batch</th>
                        <th className="px-3 py-2">Company</th>
                        <th className="px-3 py-2">Layer</th>
                        <th className="px-3 py-2">Watchlist</th>
                        <th className="px-3 py-2">Stage</th>
                        <th className="px-3 py-2">AP / MQ / VL</th>
                        <th className="px-3 py-2">Pitch</th>
                        <th className="px-3 py-2">Added On</th>
                        <th className="px-3 py-2 text-end">Actions</th>
                    </tr>
                </thead>
                <tbody className="font-13">
                    {rows.length === 0 ? (
                        <tr><td className="px-3 py-4 text-muted" colSpan={10}>No alumni match the current filters.</td></tr>
                    ) : rows.map((row) => (
                        <tr key={`${row.student_id}-${row.watchlist_code}`}>
                            <td className="px-3 py-2">
                                <AlumniName
                                    name={row.name}
                                    studentId={row.student_id}
                                    evidenceText={row.evidence_text}
                                    hasPendingReview={row.has_pending_review}
                                />
                            </td>
                            <td className="px-3 py-2"><div className="text-muted">{row.program_and_year}</div></td>
                            <td className="px-3 py-2"><div className="text-muted font-12">{row.company_name ?? '—'}</div></td>
                            <td className="px-3 py-2"><span className={`badge ${LAYER_BADGE[row.layer] || 'bg-secondary bg-opacity-10 text-secondary'} font-11`}>{LAYER_LABEL[row.layer] || row.layer}</span></td>
                            <td className="px-3 py-2">
                                {row.watchlist_label}
                                {row.is_manual_override && false && (
                                    <i className="bi bi-lock-fill ms-1 text-warning"
                                       title={`Manually set to ${row.override_type} by ${row.override_applied_by || 'admin'}${row.override_reason ? ` — ${row.override_reason}` : ''}`}></i>
                                )}
                            </td>
                            <td className="px-3 py-2">
                                <StageBadge stage={row.stage} label={row.stage_label} outcome={row.outcome} />
                            </td>
                            <td className="px-3 py-2 text-nowrap">
                                <div className="d-flex gap-1 align-items-center">
                                    <SignalChip initial="" title="Alma Mater Pride" value={row.alma_mater_pride} />
                                    <SignalChip initial="" title="Marketability" value={row.marketability} />
                                    <SignalChip initial="" title="Vibrancy" value={row.vibrancy} />
                                </div>
                            </td>
                            <td className="px-3 py-2">
                                {row.is_pitch_ready ? <span className="badge bg-primary bg-opacity-10 text-primary font-11">🎯 Phase {row.reachout_phase ?? '–'}</span> : <span className="text-muted">—</span>}
                            </td>
                            <td className="px-3 py-2 text-muted font-12 text-nowrap">{formatAddedOn(row.date_added)}</td>
                            <td className="px-3 py-2 text-end position-relative">
                                <button className="btn btn-sm btn-light border rounded-3" onClick={() => setOpenId(openId === `${row.student_id}-${row.watchlist_code}` ? null : `${row.student_id}-${row.watchlist_code}`)}>⋯</button>
                                {openId === `${row.student_id}-${row.watchlist_code}` && (
                                    <div className="position-absolute end-0 bg-white border rounded-3 shadow-sm py-1 smart-actions" style={{ zIndex: 10, minWidth: 220 }}>
                                        {actionsForRow(row).filter((a) => !(isInstituteAdmin && a.key === 'captureCommitment')).map((a) => (
                                            <div key={a.key} className="px-3 py-2 font-12 cursor-pointer text-start hover-bg-light"
                                                 onClick={() => {
                                                     setOpenId(null);
                                                     if (a.key === 'viewProfile') {
                                                         window.open(`/alumni/${row.student_id}/profile`, '_blank');
                                                     } else if (a.key === 'captureCommitment') {
                                                         window.open(`/commitment/capture/${row.institute_id}/${row.student_id}`, '_blank');
                                                     } else {
                                                         onAction(a.key, row);
                                                     }
                                                 }}>
                                                {a.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default WatchlistReportTable;
